// Polling for the two list endpoints, and knowing which of three things is true:
// we are still waiting for the first answer, we have one, or the source is down.
//
// The server already computes that distinction — `ok = error === null &&
// updatedAt > 0` in server/api.ts — so the client reads it instead of guessing
// from an error string and a local counter.

const POLL_MS = 300_000;

/**
 * How long a feed waits before asking again while asking again could change
 * what it shows: it is still starting, or its last REQUEST did not arrive. The
 * server's first refresh takes tens of seconds (a scrape plus geocoding), and
 * while it runs the server is also the slowest it ever is, so a request that
 * fails or times out is likeliest exactly then; at the steady cadence either
 * one costs the resident five minutes over a server that went green after one.
 *
 * A source the server reports as down is neither of those, and keeps the steady
 * cadence — `nextPollIn` says why.
 *
 * The faster cadence only ever asks `/api/*`, which is our own server answering
 * out of its memory — no source is polled harder for it, and a client retrying
 * its own server every 15 s is cheap.
 */
const WARMUP_MS = 15_000;

/**
 * How long one request a poll makes may take. Our own endpoints are served from
 * memory, so this bounds a hang rather than allowing work — `server/http.ts`
 * spends 8 s on GTFS-RT and 15 s on a listing out on the network.
 *
 * It is a deadline per request, so a poll may spend two of them: 20 s, longer
 * than a warm-up interval. It is not there to fit inside one. What it
 * guarantees is the single thing this feed cannot do without — that a poll
 * always ends, so the timer below is always re-armed. Nothing overlaps while it
 * runs, because that timer is armed after the poll returns and never during it.
 *
 * A standalone reading takes `READING_DEADLINE_MS` instead; the two are
 * different jobs.
 */
const DEADLINE_MS = 10_000;

/**
 * How long a STANDALONE health reading may take — one taken by a caller that
 * has no poll around it and passes no signal of its own.
 *
 * It is not `DEADLINE_MS`, and the difference is the whole point. A poll's leg
 * has a timer to re-arm, so it wants the poll over quickly; a reading has
 * nothing to re-arm and only has to END BEFORE THE NEXT READING IS TAKEN — the
 * seam asks `/api/health` every 30 s, so that is the bound.
 *
 * Ten seconds was tried and is wrong: our own server answering in 10,5 s is
 * slow, not gone, and under a 10 s bound every reading aborted, every abort
 * counted a miss, and after two of them the resident read „brak połączenia z
 * serwerem" — permanently, while `/api/vehicles` kept answering. Measured at
 * 45 s and 60 s against a server that answered every request. Saying the
 * connection is lost because one endpoint is slow is the same shape of lie as
 * blaming ZTM for our own outage (§4.3).
 *
 * What it still guarantees is the thing a deadline is for: a request that never
 * answers ends anyway, so the miss is counted and the outage is announced.
 *
 * The remaining limit is deliberate and is not this constant's to fix: a health
 * endpoint slower than the cadence still reads as a lost connection, even while
 * another endpoint of the same server answers. Whether `serverReachable` may be
 * false in that case is a seam decision, filed separately.
 */
export const READING_DEADLINE_MS = 30_000;

/**
 * Runs `use` with a signal that aborts after `ms`, and clears the timer however
 * it ends. Each request takes its own: one budget spread over both would let a
 * slow but successful first answer leave the second with no time to give one,
 * and a poll that has heard from our server twice would read `down`. The warm-up
 * window is exactly when to expect that — the server is scraping and geocoding
 * at a request a second while it answers us.
 *
 * `AbortSignal.timeout()` would say this in one line, but it runs on the
 * platform's own timers and no fake clock can drive it, so the case that matters
 * — a request that never settles must not leave the next poll unarmed — could
 * not be proved. This is the same deadline on the timer everything else uses.
 */
async function withDeadline<R>(ms: number, use: (signal: AbortSignal) => Promise<R>): Promise<R> {
  const control = new AbortController();
  const timer = setTimeout(
    () => control.abort(new DOMException(`the request took longer than ${ms} ms`, 'TimeoutError')),
    ms,
  );
  try {
    return await use(control.signal);
  } finally {
    clearTimeout(timer);
  }
}

/** Not exported: `SourceHealth` is what every caller outside this module holds,
 * and the one module that named the three states on its own was reporting a
 * state it had not been told (#153). */
type SourceState = 'starting' | 'ok' | 'down';

export type SourceHealth = {
  state: SourceState;
  /**
   * The reason, when there is one — an English string from our own server or
   * from a rejected request. It is carried to `Snapshot.health.*.error` and
   * stops there: every failure line the resident reads is Polish copy built in
   * `src/screens/layers.ts` and `src/derive/axis.ts` from `state` and `updatedAt` alone. It is
   * for us, in a console or a report, not for the screen.
   */
  error: string | null;
  /**
   * The source's own `updatedAt`, carried through for the caller; 0 when it is
   * absent. It is deliberately not part of the derivation below — `ok` already
   * folds it in — but the caller needs it, and #83 will need it more.
   */
  updatedAt: number;
};

export type Feed<T> = {
  items: T[];
  /** When we last held a real snapshot; 0 until then. */
  lastOk: number;
  /** Set once we know the source is not coming, not while it is still starting. */
  error: string | null;
  /** True exactly while the source is starting: there is no answer of any kind yet. */
  loading: boolean;
};

type Body<T> = { updatedAt: number } & Record<string, T[] | number>;

/** A source with nothing to say about itself: the answer to every unreadable health body. */
const DOWN: SourceHealth = { state: 'down', error: null, updatedAt: 0 };

/** The entry for `name`, or null when the body carries nothing usable for it. */
function entryOf(body: unknown, name: string): Record<string, unknown> | null {
  if (typeof body !== 'object' || body === null) return null;
  const sources = (body as { sources?: unknown }).sources;
  if (typeof sources !== 'object' || sources === null) return null;
  const entry = (sources as Record<string, unknown>)[name];
  // an array is an object too, and an entry that is one carries no `ok`
  if (typeof entry !== 'object' || entry === null) return null;
  return entry as Record<string, unknown>;
}

/**
 * Which of the three states the server says this source is in. An unreachable
 * /api/health, a body that will not parse and a source the server forgot to
 * report all read `down` by intent: the client cannot tell a healthy source
 * from a dead one without an answer, and claiming health it has not been told
 * about is the lie this contract exists to remove.
 *
 * „Unreachable" is `healthOf`'s `reached: false`, folded to `down` here — a
 * request that never came back and a body read the deadline cut off both. The
 * seam takes `healthOf` precisely so it can tell those from an answer; a caller
 * that does not need to is better served by one word for all of them.
 */
export async function sourceHealth(name: string, signal?: AbortSignal): Promise<SourceHealth> {
  const answer = await healthOf(name, signal);
  return answer.reached ? answer.health : DOWN;
}

/** A health reading, or the fact that the request never reached our server. */
export type HealthAnswer = { reached: false } | { reached: true; health: SourceHealth };

/**
 * The same reading, with the one fact `sourceHealth` folds away: whether the
 * request reached our server at all. `reached: false` is a `fetch` that never
 * came back — no network, or no server. Everything else is an answer, a 5xx, a
 * body that will not parse and a source the entry does not mention included:
 * those are our server telling us something, and they read `down` as before.
 *
 * `sourceHealth` keeps its contract and stays the reading most callers want.
 * The seam takes this one, because during an outage the two must not look
 * alike: a lost connection is not ZTM's fault (§4.3).
 *
 * EVERY reading is taken under `READING_DEADLINE_MS`, whatever the caller
 * passes. A caller's `signal` can only end the request SOONER — a poll's leg
 * does, at its own ten seconds, because that poll has a timer to re-arm. It
 * cannot end it later and it cannot leave it unbounded: a caller that hands in
 * a signal which never fires would otherwise be exactly the hole this rule was
 * added to close, only reached through the argument instead of past it. The
 * rule lives at the request, not at each call site: `/api/health` has four
 * callers, and a hung request at any of them is the same failure — a reading
 * that never comes back, a counter that never counts the miss, and a screen
 * that reads green through an outage (#167).
 *
 * So a poll's leg is bounded by `min(DEADLINE_MS, READING_DEADLINE_MS)` — its
 * own ten seconds, which is what it wants. Nobody should read the 30 s as
 * applying to a poll: it is the backstop under the caller's own bound, not a
 * budget the caller is given.
 *
 * That does mean a poll's leg carries two live timers, its own and this one.
 * The alternative is trusting the argument, and an argument is exactly what
 * cannot be trusted here.
 *
 * The body is read under that deadline too, and a read the deadline cut off is
 * `reached: false` — the response headers arriving is not our server having
 * answered. Calling it `reached` would hand the seam „our server answered, and
 * it knows nothing about ZTM", which is the §4.3 lie this type exists to
 * prevent: a stalled connection is not ZTM's fault.
 */
export async function healthOf(name: string, signal?: AbortSignal): Promise<HealthAnswer> {
  // `AbortSignal.any` and not `AbortSignal.timeout` for the deadline half:
  // `withDeadline` runs on the timer everything else uses, so a fake clock can
  // drive it and the case that matters can be proved (#164).
  return withDeadline(READING_DEADLINE_MS, (own) =>
    readAnswer(name, signal ? AbortSignal.any([signal, own]) : own),
  );
}

async function readAnswer(name: string, signal: AbortSignal): Promise<HealthAnswer> {
  let res: Response;
  try {
    res = await fetch('/api/health', { cache: 'no-store', signal });
  } catch {
    return { reached: false };
  }
  // a status we may not read a body from is still our server speaking
  if (!res.ok) return { reached: true, health: DOWN };
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // a body that will not parse is an answer, and reads `down` the way it
    // always has; a read our own deadline aborted never answered at all
    return signal.aborted ? { reached: false } : { reached: true, health: DOWN };
  }
  return { reached: true, health: readHealth(body, name) };
}

function readHealth(body: unknown, name: string): SourceHealth {
  const entry = entryOf(body, name);
  if (!entry || typeof entry.ok !== 'boolean') return DOWN;
  const updatedAt = typeof entry.updatedAt === 'number' ? entry.updatedAt : 0;
  if (entry.ok) return { state: 'ok', error: null, updatedAt };
  const error = typeof entry.error === 'string' && entry.error !== '' ? entry.error : null;
  // ok: false with no reason is "no refresh has succeeded yet and none has failed"
  return error ? { state: 'down', error, updatedAt } : { state: 'starting', error: null, updatedAt };
}

export function startFeed<T>(name: 'events' | 'alerts', onChange: (feed: Feed<T>) => void): void {
  const feed: Feed<T> = { items: [], lastOk: 0, error: null, loading: true };

  /** The one writer of the two derived fields, so `loading` stays `state === 'starting'`
   * and a state that is not `down` can never leave an error line on screen. */
  function setState(state: SourceState, error: string | null) {
    feed.error = state === 'down' ? (error ?? 'source unavailable') : null;
    feed.loading = state === 'starting';
  }

  /**
   * Whether the last poll failed as a REQUEST — a network error, an abort, a
   * non-200, a body that would not parse. It is not `feed.error`: a request that
   * arrives intact and reports a down source sets that too, and there is nothing
   * to hurry back for then. It is not on the feed either, because nothing
   * outside this closure has any use for it.
   */
  let requestFailed = false;

  /**
   * When to ask again. Fast while the feed has nothing to show and asking again
   * could change that: it is still starting, or we did not reach our server last
   * time. A source the server reports as down is NOT one of those — the server
   * refreshes those sources hourly, so a 15 s poll through an outage would buy
   * the resident nothing and cost every open tab a request a quarter-minute for
   * as long as it lasted.
   *
   * `loading` rather than `lastOk === 0`, because a server that restarts under a
   * feed that HAS loaded answers `updatedAt: 0` with health `starting` while
   * `lastOk` keeps the old snapshot's clock — a rule keyed on `lastOk` would
   * leave that page on „łączę się…" for five minutes, which is the defect this
   * is here to remove.
   *
   * No jitter: every one of these reads our own server out of its memory, and a
   * restart burst of them is nothing at our scale. Many thousands of open tabs
   * at once is where ±20 % would start to be worth it.
   */
  const nextPollIn = () => (feed.loading || requestFailed ? WARMUP_MS : POLL_MS);

  async function poll() {
    try {
      // a deadline each: what has to survive a request that never answers is the
      // timer armed below, and it is armed once, after both of them
      const body = await withDeadline(DEADLINE_MS, async (signal) => {
        const res = await fetch(`/api/${name}`, { cache: 'no-store', signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // the body is inside the deadline too: a response whose stream stops
        // halfway is a request that never answered
        return (await res.json()) as Body<T>;
      });
      const health = await withDeadline(DEADLINE_MS, (signal) => sourceHealth(name, signal));
      // both requests answered; whatever they said is a reading, not a failure
      requestFailed = false;
      if (body.updatedAt > 0) {
        // the payload keys carry either the list or a number, so this is a check,
        // not an assertion: anything that is not a list reads as an empty day
        const items = body[name];
        feed.items = Array.isArray(items) ? items : [];
        // the age of the data, not of the request: "dane z HH:MM"
        // has to be true of the snapshot the resident is looking at
        feed.lastOk = body.updatedAt;
        setState(health.state === 'down' ? 'down' : 'ok', health.error);
      } else {
        // no snapshot yet. Health says which of the two this is, and it says so
        // for as long as it takes: there is no budget left to run out.
        // `ok` here is a server that has a snapshot we have not been handed yet,
        // which is one more poll away and not a failure either.
        setState(health.state === 'down' ? 'down' : 'starting', health.error);
      }
    } catch (e) {
      // an abort is a request that did not answer, which is what a network error
      // is too: the same missed poll, not a state of its own
      requestFailed = true;
      setState('down', String(e));
    }
    notify();
    setTimeout(tick, nextPollIn());
  }

  /**
   * Rendering is not fetching. A DOM bug in a list or map renderer used to
   * reject poll(), land in the tick handler and set `feed.error`, so the UI told
   * the resident the source was down while the source was fine — and the real
   * stack never surfaced. It is logged as what it is instead.
   */
  function notify() {
    try {
      onChange(feed);
    } catch (e) {
      console.error(`${name}: rendering the feed failed`, e);
    }
  }

  /** poll() handles its own failures; this keeps a rejection escaping it out of the
   * unhandled-rejection path and keeps the timer alive. */
  const tick = () => {
    poll().catch((e: unknown) => {
      feed.error = String(e);
      feed.loading = false;
      notify();
      setTimeout(tick, nextPollIn());
    });
  };

  notify();
  tick();
}
